import { watch } from 'vue';
import { useRoute } from 'vue-router';

import { storeToRefs } from 'pinia';

import { useHangUpCommon } from '@/composables/domains/chat/useHangupCommon';
import { useNotification } from '@/composables/shared/useNotification';
import { useAudioStore } from '@/stores/audio';
import { useSocketStore } from '@/stores/socket';
import { useUserStore } from '@/stores/user';
import { LanguageCode } from '@/types/common';
import { ConnectionInfoSingle } from '@/types/socket';
import { getBrowserAndDeviceInfo } from '@/utils/device';
import { stringToBase64UrlSafe } from '@/utils/encryptor.js';

export function useSocketSingle(setBottomBarStatus?: (status: number) => void) {
  const audioStore = useAudioStore();
  const socketStore = useSocketStore();
  const userStore = useUserStore();

  const route = useRoute();

  const { selectedUserId, selectedIsVoiceRegistered, selectedChatId, selectedNickname } = storeToRefs(socketStore);

  const { handleHangUp } = useHangUpCommon({
    setBottomBarStatus: setBottomBarStatus ?? (() => {}),
  });

  const { isMicOn } = storeToRefs(audioStore);

  // 웹소켓 연결
  const connectToWebsocket = async (isForceful = false, languageSet) => {
    if (setBottomBarStatus) setBottomBarStatus(1);

    const isCj = route.params.partnerCode === 'cj';

    const userId = userStore.userInfo.userId;

    if (!audioStore.isInputSelected()) await audioStore.initDevices();

    const agent = JSON.stringify(await getBrowserAndDeviceInfo()); // 사용자 브라우저 및 장치 정보
    const authorization = localStorage.getItem('authToken')
      ? `Bearer ${localStorage.getItem('authToken')}`
      : `SC-USER-ID ${localStorage.getItem('SC-USER-ID')}`;

    try {
      const languageCode: LanguageCode = (localStorage.getItem('language') || 'en_US') as LanguageCode;
      audioStore.selectLanguage(languageCode);
    } catch (error) {
      console.error('선택된 언어 조회를 실패했습니다.', error);
    }

    selectedChatId.value = userId as string;
    selectedUserId.value = userId as string;
    selectedNickname.value = userId as string;

    const connectionInfo: ConnectionInfoSingle = {
      userId: userId as string,
      isRegister: selectedIsVoiceRegistered.value,
      isTssOn: '0',
      voiceId: '0',
      chatId: userId as string,
      languageCode: languageSet.languageHome.code,
      languageCodeAway: languageSet.languageAway.code,
      nickname: stringToBase64UrlSafe(userId),
      isPartnerPerson: '1',
      longVoice: '1',
      code: isCj ? (route.params.partnerCode as string) : (userId as string),
      token: authorization,
      evictMySelf: '1',
      callType: 'SINGLE',
      connectionType: 'SINGLE',
      agent,
    };
    socketStore.setConnectInfo(connectionInfo);
    socketStore.setupWebSocket(isForceful);
  };

  const { alertExceeded } = useNotification();

  // 사용량 초과 시
  watch(
    () => socketStore.exceededUsageLimit,
    (exceededUsageLimit) => {
      if (exceededUsageLimit) {
        handleHangUp();
        alertExceeded();
      }
    }
  );

  // 소켓 재연결 시
  watch(
    () => socketStore.recover,
    async (recover) => {
      if (recover) {
        isMicOn.value = false;
        setBottomBarStatus!(-2);
      }
    },
    { immediate: true }
  );

  return {
    connectToWebsocket,
  };
}
